import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Chip,
    Typography,
    Avatar,
} from "@mui/material";
import { Podcasts, Circle, Computer, PhoneIphone } from "@mui/icons-material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import presenceService from "../../services/presenceService";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";

dayjs.extend(relativeTime);

const POLL_INTERVAL_MS = 7_000;

// A rough browser/OS read off the user-agent string — good enough for an
// at-a-glance admin view, not meant to be a precise device-detection lib.
const summarizeUserAgent = (ua = "") => {
    const browser = /Edg\//.test(ua)
        ? "Edge"
        : /Chrome\//.test(ua)
          ? "Chrome"
          : /Firefox\//.test(ua)
            ? "Firefox"
            : /Safari\//.test(ua) && !/Chrome\//.test(ua)
              ? "Safari"
              : "Browser";
    const os = /Windows/.test(ua)
        ? "Windows"
        : /Mac OS/.test(ua)
          ? "Mac"
          : /Android/.test(ua)
            ? "Android"
            : /iPhone|iPad/.test(ua)
              ? "iOS"
              : /Linux/.test(ua)
                ? "Linux"
                : "";
    return [browser, os].filter(Boolean).join(" · ");
};

const SessionsTable = ({ title, icon, sessions, loading }) => (
    <Card>
        <CardContent sx={{ pb: 1 }}>
            <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center", gap: 0.75, textTransform: "uppercase", letterSpacing: 0.4 }}
            >
                {icon}
                {title} — {sessions.length} online
            </Typography>
        </CardContent>
        {loading ? (
            <SkeletonLoader rows={4} />
        ) : sessions.length === 0 ? (
            <EmptyState icon="📡" title="Nobody's online" description={`No ${title.toLowerCase()} sessions right now.`} />
        ) : (
            <Box sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: 600 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>User</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Current Page</TableCell>
                            <TableCell>Device</TableCell>
                            <TableCell>Last Seen</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sessions.map((s) => (
                            <TableRow key={s._id} hover>
                                <TableCell>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <Avatar src={s.user?.profilePhotoUrl} sx={{ width: 30, height: 30, fontSize: 13 }}>
                                            {s.user?.firstName?.[0]}
                                            {s.user?.lastName?.[0]}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2">
                                                {s.user ? `${s.user.firstName || ""} ${s.user.lastName || ""}`.trim() : "—"}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {s.user?.email}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={s.role}
                                        color={s.role === "admin" ? "primary" : "default"}
                                        variant="outlined"
                                    />
                                </TableCell>
                                <TableCell sx={{ fontSize: 12.5 }}>{s.pageLabel || s.page}</TableCell>
                                <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                                    {summarizeUserAgent(s.userAgent)}
                                </TableCell>
                                <TableCell sx={{ fontSize: 12, color: "text.secondary", whiteSpace: "nowrap" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <Circle sx={{ fontSize: 8, color: "success.main" }} />
                                        {dayjs(s.lastSeenAt).fromNow()}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        )}
    </Card>
);

// Live "who's online right now" dashboard — backed by LiveSession, a
// heartbeat-per-user collection with a TTL index (see
// server/models/LiveSession.js) rather than a request log, so this always
// reflects genuinely active sessions with no cleanup logic needed here.
// Polls every few seconds rather than a websocket push — see the approach
// note from when this was scoped: simpler to deploy, no sticky-session
// requirement on the load balancer, and a few seconds of staleness is fine
// for this use case.
const AdminLiveTraffic = () => {
    const [sessions, setSessions] = useState([]);
    const [pageBreakdown, setPageBreakdown] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const { data } = await presenceService.getLive();
            setSessions(data.data.sessions || []);
            setPageBreakdown(data.data.pageBreakdown || []);
        } catch {
            // Leave the previous snapshot on screen rather than flashing empty
            // on a transient network hiccup — this page polls again shortly.
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const interval = setInterval(load, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, []);

    // Missing `platform` (a session written before this field existed, or
    // pre-migration) defaults to "web" — matches the schema's own default.
    const webSessions = sessions.filter((s) => (s.platform || "web") === "web");
    const mobileSessions = sessions.filter((s) => s.platform === "mobile");

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <PageHeader
                title="Live Traffic"
                subtitle="Who's on the portal right now, and what they're looking at"
            />

            {/* Plain Box/CSS-grid, not MUI's <Grid> — this app is on MUI
                v9, where <Grid> dropped the old item/xs/sm props for a
                `size` prop; the old props here were silently ignored,
                collapsing this into a single stacked column regardless of
                screen size instead of the intended 1:2 row on sm+. */}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 2fr" }, gap: 2 }}>
                <Card>
                    <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Podcasts color="success" />
                        <Box>
                            <Typography variant="h4" fontWeight={700}>
                                {sessions.length}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Online right now
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
                <Card sx={{ height: "100%" }}>
                    <CardContent>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                            Most-viewed pages right now
                        </Typography>
                        {pageBreakdown.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                Nobody online.
                            </Typography>
                        ) : (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {pageBreakdown.slice(0, 8).map((p) => (
                                    <Chip
                                        key={p.page}
                                        size="small"
                                        label={`${p.page} · ${p.count}`}
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Box>

            <SessionsTable title="Web" icon={<Computer sx={{ fontSize: 15 }} />} sessions={webSessions} loading={loading} />
            <SessionsTable title="Mobile" icon={<PhoneIphone sx={{ fontSize: 15 }} />} sessions={mobileSessions} loading={loading} />
        </Box>
    );
};

export default AdminLiveTraffic;
